import React, { useState } from "react";
import {
  Box,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  useGetSharedNotesQuery,
  useUnshareNoteMutation,
} from "../../../../../app/notesApiSlice";
import NotePreviewContent from "./NotePreviewContent";

const SharedNotes = () => {
  const [sortBy, setSortBy] = useState("topic");
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [openUnshareModal, setOpenUnshareModal] = useState({});
  const [openPreviewModals, setOpenPreviewModals] = useState({});
  const [deletedModals, setDeletedModals] = useState({});

  const { data, isLoading } = useGetSharedNotesQuery();
  const [unshareNote, { isLoading: isDeletingNote }] = useUnshareNoteMutation();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "Invalid date";
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const sortedNotes = React.useMemo(() => {
    if (!data?.notes) return [];
    return [...data.notes].sort((a, b) => {
      const noteA = a.note;
      const noteB = b.note;
      if (sortBy === "level") {
        return noteA.level.localeCompare(noteB.level);
      } else if (sortBy === "subject") {
        return noteA.subject.localeCompare(noteB.subject);
      } else if (sortBy === "topic") {
        return noteA.topic.localeCompare(noteB.topic);
      }
      return 0;
    });
  }, [data, sortBy]);

  const paginatedNotes = React.useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return sortedNotes.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedNotes, page]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeSort = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const totalNotes = sortedNotes.length;
  const startIndex = (page - 1) * itemsPerPage + 1;
  const endIndex = Math.min(page * itemsPerPage, totalNotes);

  const handleOpenUnshareModal = (noteId) => {
    setOpenUnshareModal((prev) => ({ ...prev, [noteId]: true }));
  };

  const handleOpenPreviewModal = (noteId) => {
    setOpenPreviewModals((prev) => ({ ...prev, [noteId]: true }));
  };

  const handleCloseUnshareModal = (noteId) => {
    setOpenUnshareModal((prev) => ({ ...prev, [noteId]: false }));
  };

  const handleUnshare = async (noteId) => {
    try {
      await unshareNote({ id: noteId }).unwrap();
      handleCloseUnshareModal(noteId);
      setDeletedModals((prev) => ({ ...prev, [noteId]: true }));
    } catch (error) {
      console.error(error);
    }
  };

  const handleCloseDeletedModal = (noteId) => {
    setDeletedModals((prev) => ({ ...prev, [noteId]: false }));
  };

  const handleClosePreviewModal = (noteId) => {
    setOpenPreviewModals((prev) => ({ ...prev, [noteId]: false }));
  };

  return (
    <Box>
      <Box sx={{ marginTop: "2em" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1em",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "1rem", md: "1.25rem" },
              fontWeight: 700,
              letterSpacing: "-1px",
            }}
          >
            Shared by Me
          </Typography>
          <Select value={sortBy} onChange={handleChangeSort} size="small">
            <MenuItem value="topic">Sort by: Topic</MenuItem>
            <MenuItem value="subject">Sort by: Subject</MenuItem>
            <MenuItem value="level">Sort by: Level</MenuItem>
          </Select>
        </Box>

        {isLoading && <LinearProgress />}

        {paginatedNotes.length === 0 && !isLoading && (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              color: "text.secondary",
            }}
          >
            <Typography variant="body1">No shared notes found</Typography>
          </Box>
        )}

        {paginatedNotes.map((note) => (
          <Card key={note.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                  {note.note.topic}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {formatDate(note.note.updated_at)}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  <strong>Subject:</strong> {note.note.subject}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  <strong>Level:</strong> {note.note.level}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Shared on: {formatDate(note.shared_at)}
                </Typography>
              </Box>
              <Box
                sx={{
                  maxHeight: "100px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "text.secondary",
                  fontSize: "0.875rem",
                  "& p": { margin: 0 },
                  "& h1, & h2, & h3, & h4, & h5, & h6": { marginTop: 0, marginBottom: "0.5em", fontSize: "1em" },
                }}
                dangerouslySetInnerHTML={{ __html: note.note.body }}
              />
            </CardContent>
            <CardActions sx={{ justifyContent: "flex-end" }}>
              <Button
                size="small"
                onClick={() => handleOpenPreviewModal(note.id)}
              >
                Preview
              </Button>
              <Button
                size="small"
                onClick={() => handleOpenUnshareModal(note.id)}
                color="error"
              >
                Remove
              </Button>
            </CardActions>

            {/* Unshare Modal */}
            <Dialog
              open={openUnshareModal[note.id] || false}
              onClose={() => handleCloseUnshareModal(note.id)}
            >
              <DialogTitle>Remove Note</DialogTitle>
              <DialogContent>
                <Typography>
                  Are you sure you want to remove this note? This action cannot be undone.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => handleCloseUnshareModal(note.id)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => handleUnshare(note.id)}
                  color="error"
                  disabled={isDeletingNote}
                >
                  {isDeletingNote ? "Removing..." : "Remove"}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Preview Modal */}
            <Dialog
              open={openPreviewModals[note.id] || false}
              onClose={() => handleClosePreviewModal(note.id)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>{note.note.topic}</DialogTitle>
              <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Subject: {note.note.subject}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Level: {note.note.level}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {formatDate(note.note.updated_at)}
                  </Typography>
                </Box>
                <NotePreviewContent note={note.note} />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => handleClosePreviewModal(note.id)}>
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            {/* Remove Success Modal */}
            <Dialog
              open={deletedModals[note.id] || false}
              onClose={() => handleCloseDeletedModal(note.id)}
            >
              <DialogTitle>Success</DialogTitle>
              <DialogContent>
                <Typography>Note removed successfully.</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => handleCloseDeletedModal(note.id)}>
                  OK
                </Button>
              </DialogActions>
            </Dialog>
          </Card>
        ))}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "2em",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {startIndex} to {endIndex} of {totalNotes} entries
          </Typography>

          <Pagination
            count={Math.ceil(sortedNotes.length / itemsPerPage)}
            page={page}
            onChange={handleChangePage}
            color="primary"
            shape="rounded"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SharedNotes;
